import {
  CATS_MER, MARCAS_EN_DOS_CATEGORIAS, RECOMIENDA_SIN_RESPUESTA,
  esMarcaPg, etiquetaCat, type CategoriaMer, type PresenciaMer,
} from './catalogo';
import { claveTexto, esDeProvincia, STOPWORDS_LUGAR } from './normalizar';
import type { FichaMercado, CierreMercado, PrecioMer } from './tipos';

/**
 * Los cálculos del levantamiento de mercados.
 *
 * ## La regla que gobierna este archivo
 *
 * **Todo indicador viaja con su denominador.** No como nota al pie: adentro del objeto, con nombre,
 * para que la pantalla no tenga forma de dibujar el numerador solo. Un porcentaje sin denominador
 * a la vista no se puede revisar, y lo que no se puede revisar termina citado en una reunión.
 *
 * Los denominadores de acá **no son todos el mismo número**, y esa es justamente la parte difícil:
 *
 * - La cobertura va sobre los puestos **visitados** (100), no sobre los levantados (89). Un puesto
 *   cerrado es un dato: sin él, «30 visitados y 8 levantados» se lee como un recorrido de 8.
 * - La presencia de una marca va sobre las fichas levantadas **que declararon esa categoría**. El
 *   formulario solo pregunta por lo que el puesto dijo vender, así que preguntar por Ariel en un
 *   puesto que solo vende shampoo nunca pasó.
 * - El share de recomendación va sobre las fichas **que contestaron**, y `No contestó` es un valor
 *   guardado que hay que sacar a mano.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Presencia
// ═══════════════════════════════════════════════════════════════════════════

export interface FilaPresencia {
  cat: CategoriaMer;
  marca: string;
  esPg: boolean;
  /** Fichas levantadas que declararon esta categoría. El denominador de la fila. */
  de: number;
  presente: number;
  quiebre: number;
  noTrabaja: number;
  /** Declararon la categoría y no contestaron por esta marca. */
  sinRespuesta: number;
  pctPresente: number;
  /** `Bolívar` vive en dos categorías y el formulario lo guarda una sola vez. */
  ambigua: boolean;
}

/**
 * Presencia marca por marca, con el denominador correcto por categoría.
 *
 * 🔴 **El numerador y el denominador salen del MISMO conjunto**, y hace falta decirlo porque el
 * camino corto no lo hace: contar «cuántas fichas tienen Bolívar en `presencia`» contra «cuántas
 * declararon detergentes» mezcla dos conjuntos, porque Bolívar también es marca de suavizantes y
 * una ficha que solo declaró suavizantes entraría al numerador sin estar en el denominador. Con la
 * data de hoy eso da **60 de 60 = 100 %** cuando lo cierto es 58/60.
 *
 * Acá cada fila se calcula recorriendo únicamente las fichas de su denominador, así que el caso no
 * puede darse. `Bolívar` aparece en sus dos categorías, cada una con su cuenta honesta, y marcado
 * `ambigua` para que la pantalla explique que la respuesta que alimenta las dos filas es la misma.
 */
export function presenciaPorMarca(fichas: readonly FichaMercado[]): FilaPresencia[] {
  const levantadas = fichas.filter((f) => f.levantado);
  const filas: FilaPresencia[] = [];

  for (const cat of CATS_MER) {
    const delCat = levantadas.filter((f) => (f.ficha.cats ?? []).includes(cat.id));
    if (delCat.length === 0) continue;
    for (const marca of [...cat.pg, ...cat.comp]) {
      let presente = 0; let quiebre = 0; let noTrabaja = 0; let sinRespuesta = 0;
      for (const f of delCat) {
        const v = f.ficha.presencia?.[marca];
        if (v === 'presente') presente += 1;
        else if (v === 'quiebre') quiebre += 1;
        else if (v === 'no_trabaja') noTrabaja += 1;
        else sinRespuesta += 1;
      }
      const de = delCat.length;
      filas.push({
        cat, marca, esPg: esMarcaPg(marca), de,
        presente, quiebre, noTrabaja, sinRespuesta,
        pctPresente: de === 0 ? 0 : Math.round((presente / de) * 100),
        ambigua: MARCAS_EN_DOS_CATEGORIAS.includes(marca),
      });
    }
  }
  return filas;
}

/** Marcas distintas con alguna respuesta de presencia. Deduplica `Bolívar`. */
export function marcasMedidas(fichas: readonly FichaMercado[]): number {
  const vistas = new Set<string>();
  for (const f of fichas) {
    for (const m of Object.keys(f.ficha.presencia ?? {})) vistas.add(m);
  }
  return vistas.size;
}

/**
 * Mediciones de presencia de categorías que la ficha terminó **no declarando**.
 *
 * El formulario avisa («ya habías anotado datos de X y esa categoría quedó desmarcada») pero deja
 * enviar, y no borra nada. Son respuestas reales que no pertenecen al denominador de ninguna fila,
 * así que se cuentan aparte en vez de desaparecer: la suma tiene que cerrar.
 */
export function presenciasHuerfanas(fichas: readonly FichaMercado[]): number {
  let n = 0;
  for (const f of fichas) {
    const cats = new Set(f.ficha.cats ?? []);
    for (const marca of Object.keys(f.ficha.presencia ?? {})) {
      const suyas = CATS_MER.filter((c) => c.pg.includes(marca) || c.comp.includes(marca));
      if (suyas.length > 0 && !suyas.some((c) => cats.has(c.id))) n += 1;
    }
  }
  return n;
}

// ═══════════════════════════════════════════════════════════════════════════
// Precios
// ═══════════════════════════════════════════════════════════════════════════

/** El nombre con el que se muestra un precio: la marca, la que escribió, o el cubo «sin marca». */
export function marcaDePrecio(p: PrecioMer): { nombre: string; esMarca: boolean } {
  const otra = (p.marca_otra ?? '').trim();
  if (p.marca === 'Otra' && otra !== '') return { nombre: otra, esMarca: false };
  const m = (p.marca ?? '').trim();
  if (m === '' || m === 'Otra') return { nombre: 'sin marca', esMarca: false };
  return { nombre: m, esMarca: true };
}

/**
 * El precio por unidad, que **no se guarda**: el formulario lo muestra y lo descarta.
 *
 * 🔴 Devuelve `null` cuando `unidades` es 1, y no el precio.
 *
 * De 682 precios levantados, **307 traen `unidades = 1`**, y no todos son sachets sueltos: hay 13
 * paquetes de Babysec cargados como «1 unidad» a S/ 30–46 conviviendo con 17 del mismo paquete
 * cargados con sus 41–58 unidades a S/ 0,52–0,98. Es el mismo producto anotado de dos maneras.
 *
 * Si los dos entran a la misma columna, la mediana de Babysec sale S/ 0,86 con un rango de
 * S/ 0,52 a S/ 46,00, y esos 13 no son valores atípicos que la mediana absorba: son un tercio de
 * la muestra. La mediana protege contra un tipeo suelto, no contra dos sistemas de unidades
 * conviviendo bajo el mismo encabezado.
 *
 * Por eso el precio por unidad solo existe cuando hay más de una unidad, y el precio del paquete
 * se reporta aparte y con ese nombre.
 */
export function precioPorUnidad(precio: number | null, unidades: number | null): number | null {
  if (precio === null || unidades === null) return null;
  if (!Number.isFinite(precio) || !Number.isFinite(unidades)) return null;
  if (unidades <= 1) return null;
  return precio / unidades;
}

const mediana = (xs: readonly number[]): number | null => {
  if (xs.length === 0) return null;
  const o = [...xs].sort((a, b) => a - b);
  const m = Math.floor(o.length / 2);
  return o.length % 2 === 1 ? o[m] : (o[m - 1] + o[m]) / 2;
};

export interface FilaPrecio {
  catId: string;
  catNom: string;
  marca: string;
  esMarca: boolean;
  esPg: boolean;
  n: number;
  /** Mediana del precio tal cual se levantó. Es lo único que se midió sin interpretar. */
  medianaPrecio: number | null;
  minPrecio: number | null;
  maxPrecio: number | null;
  /** Sobre cuántos de los `n` se pudo calcular el precio por unidad (los de `unidades > 1`). */
  conUnidades: number;
  medianaPorUnidad: number | null;
  minPorUnidad: number | null;
  maxPorUnidad: number | null;
  /** Filas cargadas como «1 unidad»: el número es el precio del PAQUETE, no el de la unidad. */
  comoPaquete: number;
}

export function preciosPorMarca(fichas: readonly FichaMercado[]): FilaPrecio[] {
  const acc = new Map<string, {
    catId: string; marca: string; esMarca: boolean;
    precios: number[]; porUnidad: number[]; comoPaquete: number; n: number;
  }>();

  for (const f of fichas) {
    for (const p of f.ficha.precios ?? []) {
      const { nombre, esMarca } = marcaDePrecio(p);
      const k = `${p.cat}||${nombre}`;
      const e = acc.get(k) ?? {
        catId: p.cat, marca: nombre, esMarca,
        precios: [], porUnidad: [], comoPaquete: 0, n: 0,
      };
      e.n += 1;
      if (p.precio !== null && Number.isFinite(p.precio)) e.precios.push(p.precio);
      const pu = precioPorUnidad(p.precio, p.unidades);
      if (pu !== null) e.porUnidad.push(pu);
      else if (p.unidades === 1) e.comoPaquete += 1;
      acc.set(k, e);
    }
  }

  return [...acc.values()].map((e) => ({
    catId: e.catId,
    catNom: etiquetaCat(e.catId),
    marca: e.marca,
    esMarca: e.esMarca,
    esPg: e.esMarca && esMarcaPg(e.marca),
    n: e.n,
    medianaPrecio: mediana(e.precios),
    minPrecio: e.precios.length > 0 ? Math.min(...e.precios) : null,
    maxPrecio: e.precios.length > 0 ? Math.max(...e.precios) : null,
    conUnidades: e.porUnidad.length,
    medianaPorUnidad: mediana(e.porUnidad),
    minPorUnidad: e.porUnidad.length > 0 ? Math.min(...e.porUnidad) : null,
    maxPorUnidad: e.porUnidad.length > 0 ? Math.max(...e.porUnidad) : null,
    comoPaquete: e.comoPaquete,
  })).sort((a, b) => a.catNom.localeCompare(b.catNom, 'es') || b.n - a.n);
}

/**
 * Precios de una marca que la ficha **no marcó como presente**.
 *
 * ⚠️ La premisa fácil —«el formulario solo deja cargar precios de marcas presentes»— es falsa: eso
 * vale para el atajo «agregar las N marcas que dijiste que tiene», pero «+ Agregar un precio a
 * mano» ofrece **todas** las marcas de la categoría sin mirar la presencia. Así que la regla no es
 * «quedó en quiebre o no la trabaja» sino **«no está en presente»**, que además cubre el caso que
 * de verdad ocurre: la marca sin respuesta de presencia.
 *
 * No se descartan del total de la categoría —el precio se levantó de verdad— pero se listan, que
 * es lo que permite revisarlos.
 */
export function preciosSinPresencia(
  fichas: readonly FichaMercado[],
): { fichaId: string; marca: string; cat: string; estado: string }[] {
  const out: { fichaId: string; marca: string; cat: string; estado: string }[] = [];
  for (const f of fichas) {
    for (const p of f.ficha.precios ?? []) {
      const { nombre, esMarca } = marcaDePrecio(p);
      if (!esMarca) continue;
      const v = f.ficha.presencia?.[nombre];
      if (v === 'presente') continue;
      out.push({
        fichaId: f.id, marca: nombre, cat: etiquetaCat(p.cat),
        estado: v === undefined ? 'sin respuesta de presencia' : v,
      });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Recomendación, abastecimiento, clientes
// ═══════════════════════════════════════════════════════════════════════════

export interface Conteo { valor: string; n: number }

const contar = (xs: readonly string[]): Conteo[] => {
  const m = new Map<string, number>();
  for (const x of xs) m.set(x, (m.get(x) ?? 0) + 1);
  return [...m.entries()].map(([valor, n]) => ({ valor, n }))
    .sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor, 'es'));
};

export interface Recomendaciones {
  /** Las que son marcas de verdad. El share se lee sobre `de`. */
  marcas: Conteo[];
  /** `La más barata` y lo que se escribió en «Otra»: respuestas válidas que no son marcas. */
  otrasRespuestas: Conteo[];
  motivos: Conteo[];
  /** Denominador del share: fichas que contestaron algo que no sea «No contestó». */
  de: number;
  /** Contestaron literalmente «No contestó». Es un valor guardado, no un vacío. */
  noContesto: number;
  /** Ni siquiera se les preguntó, o quedó en blanco. */
  sinDato: number;
}

/**
 * El share de recomendación, con `No contestó` afuera del numerador **y del denominador**.
 *
 * 🔴 `No contestó` es una opción de la lista del formulario, o sea un valor guardado. Con la data
 * de hoy son 7 fichas, y contándolo queda **cuarto**, arriba de Sedal y de Sapolio, en la tabla
 * que el cliente lee como «qué recomienda el puestero». Una no-respuesta no compite con las
 * marcas: no es que el puestero recomiende no contestar.
 *
 * ⚠️ Y hay un límite que ningún cálculo arregla, así que la pantalla tiene que decirlo: **la lista
 * de marcas que se le ofreció a cada puestero depende de lo que declaró vender**, así que el
 * denominador de cada marca no es el mismo. El share sirve para ver el orden, no para comparar dos
 * marcas de categorías distintas.
 */
export function recomendaciones(fichas: readonly FichaMercado[]): Recomendaciones {
  const levantadas = fichas.filter((f) => f.levantado);
  const marcas: string[] = [];
  const otras: string[] = [];
  const motivos: string[] = [];
  let noContesto = 0;
  let sinDato = 0;

  for (const f of levantadas) {
    const r = f.ficha.recomienda;
    const m = (r?.marca ?? '').trim();
    if (m === '') { sinDato += 1; continue; }
    if (m === RECOMIENDA_SIN_RESPUESTA) { noContesto += 1; continue; }
    if (m === 'Otra') {
      const otra = (r?.marca_otra ?? '').trim();
      otras.push(otra === '' ? 'Otra (sin especificar)' : otra);
    } else if (m === 'La más barata') {
      otras.push('La más barata');
    } else {
      marcas.push(m);
    }
    const mot = (r?.motivo ?? '').trim();
    if (mot !== '') motivos.push(mot);
  }

  return {
    marcas: contar(marcas),
    otrasRespuestas: contar(otras),
    motivos: contar(motivos),
    de: marcas.length + otras.length,
    noContesto,
    sinDato,
  };
}

export interface Abastecimiento {
  donde: Conteo[];
  frecuencia: Conteo[];
  visita: Conteo[];
  /** Denominador: fichas con al menos un origen marcado. `donde` es MÚLTIPLE: la suma pasa de `de`. */
  de: number;
  sinDato: number;
  /** Nombres de brókers escritos a mano. Es texto crudo, no una métrica. */
  brokers: Conteo[];
}

export function abastecimiento(fichas: readonly FichaMercado[]): Abastecimiento {
  const levantadas = fichas.filter((f) => f.levantado);
  const donde: string[] = [];
  const frecuencia: string[] = [];
  const visita: string[] = [];
  const brokers: string[] = [];
  let de = 0;

  for (const f of levantadas) {
    const a = f.ficha.abasto;
    const d = a?.donde ?? [];
    if (d.length > 0) { de += 1; donde.push(...d); }
    const fr = (a?.frecuencia ?? '').trim();
    if (fr !== '') frecuencia.push(fr);
    const v = (a?.visita ?? '').trim();
    visita.push(v === '' ? 'no se preguntó' : v);
    const q = (a?.quien ?? '').trim();
    if (q !== '') brokers.push(q);
  }

  return {
    donde: contar(donde),
    frecuencia: contar(frecuencia),
    visita: contar(visita),
    de,
    sinDato: levantadas.length - de,
    brokers: contar(brokers),
  };
}

export function tipoDeCliente(
  fichas: readonly FichaMercado[],
): { conteo: Conteo[]; de: number; sinDato: number } {
  const levantadas = fichas.filter((f) => f.levantado);
  const todos: string[] = [];
  let de = 0;
  for (const f of levantadas) {
    const c = f.ficha.clientes ?? [];
    if (c.length > 0) { de += 1; todos.push(...c); }
  }
  return { conteo: contar(todos), de, sinDato: levantadas.length - de };
}

export function categoriasDeclaradas(fichas: readonly FichaMercado[]): Conteo[] {
  const todas: string[] = [];
  for (const f of fichas.filter((x) => x.levantado)) {
    for (const c of f.ficha.cats ?? []) todas.push(etiquetaCat(c));
  }
  return contar(todas);
}

/** Lo que el campo vio y el catálogo cerrado no tenía. Texto crudo: se lista, no se cuenta. */
export function fueraDeLista(fichas: readonly FichaMercado[]): Conteo[] {
  const xs = fichas
    .map((f) => (f.ficha.cierre?.fuera_de_lista ?? '').trim())
    .filter((t) => t !== '');
  return contar(xs);
}

// ═══════════════════════════════════════════════════════════════════════════
// Cobertura
// ═══════════════════════════════════════════════════════════════════════════

export interface Cobertura {
  /** El denominador de todo lo de esta caja. */
  visitados: number;
  levantados: number;
  noSePudo: number;
  /** Los motivos, **con un cubo «sin motivo»**: `motivo_no` puede venir en NULL. */
  motivos: Conteo[];
  escritosAMano: number;
  conFoto: number;
  /** Mercados-día con al menos una ficha. Es el denominador de las jornadas cerradas. */
  mercadoDias: number;
  jornadasCerradas: number;
}

/**
 * La cobertura del recorrido.
 *
 * 🔴 **El denominador son los visitados, no los levantados.** El propio formulario lo explica en la
 * pregunta: *«Un puesto cerrado también es un dato. Si no lo mandás, un recorrido de treinta
 * puestos con ocho medidos se lee como un recorrido de ocho.»* La pantalla no puede deshacer eso
 * dividiendo por 89.
 *
 * ⚠️ **Las jornadas cerradas se cruzan por clave normalizada de los dos lados.** `mer_cierres` se
 * guarda con el texto crudo del mercado, igual que las fichas, así que cruzar crudo contra
 * normalizado —o al revés— da un numerador y un denominador que no se pueden comparar. Con la data
 * de hoy: los cierres dicen `Capullanas` y `Mercado Piura`, mientras las fichas del mismo mercado y
 * el mismo día dicen `Mercado central de Piura`. Cruzando crudo, nueve fichas con jornada cerrada
 * aparecerían como si no la tuvieran.
 */
export function cobertura(
  fichas: readonly FichaMercado[], cierres: readonly CierreMercado[],
): Cobertura {
  const visitados = fichas.length;
  const levantados = fichas.filter((f) => f.levantado).length;
  const noLevantadas = fichas.filter((f) => !f.levantado);

  const motivos = contar(noLevantadas.map((f) => {
    const m = (f.motivoNo ?? '').trim();
    return m === '' ? 'sin motivo' : m;
  }));

  const clave = (m: string | null, fecha: string): string =>
    `${claveTexto(m, STOPWORDS_LUGAR) ?? '?'}||${fecha}`;

  const diasConFicha = new Set(fichas.map((f) => clave(f.mercadoNombre, f.fecha)));
  const diasCerrados = new Set(cierres.map((c) => clave(c.mercadoNombre, c.fecha)));
  let cerradas = 0;
  for (const d of diasConFicha) if (diasCerrados.has(d)) cerradas += 1;

  return {
    visitados,
    levantados,
    noSePudo: noLevantadas.length,
    motivos,
    escritosAMano: fichas.filter((f) => f.ficha.mercado_escrito_a_mano === true).length,
    conFoto: fichas.filter((f) => f.fotos.length > 0).length,
    mercadoDias: diasConFicha.size,
    jornadasCerradas: cerradas,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Contradicciones
// ═══════════════════════════════════════════════════════════════════════════

export interface Contradiccion { clave: string; texto: string; tono: 'aviso' | 'grave' }

/**
 * Lo que la ficha dice de sí misma y no cierra.
 *
 * No son errores del sistema: son cosas que pasaron en campo y que cambian cómo se lee el dato. Se
 * muestran para que quien revisa decida, no para bloquear nada.
 *
 * ⚠️ Deliberadamente **no** está la alerta de «precio por unidad fuera de rango». Sin calibrar por
 * marca salta demasiado —la equivalente del estudio de Softys marcaba el 57 % de las visitas— y una
 * alerta que salta siempre enseña a ignorarlas todas, incluidas las que importan.
 */
export function contradiccionesDeFicha(f: FichaMercado): Contradiccion[] {
  const out: Contradiccion[] = [];

  if (!f.levantado) {
    const cargo = (f.ficha.cats ?? []).length > 0
      || Object.keys(f.ficha.presencia ?? {}).length > 0
      || (f.ficha.precios ?? []).length > 0;
    if (cargo) {
      out.push({
        clave: 'no_levantado_con_datos', tono: 'grave',
        texto: 'Se cargaron datos y después se marcó «No pude». El formulario no borra nada al '
          + 'cambiar la respuesta, así que lo de abajo se levantó de verdad.',
      });
    }
    if ((f.motivoNo ?? '').trim() === '') {
      out.push({
        clave: 'sin_motivo', tono: 'aviso',
        texto: 'No se pudo levantar y no se dijo por qué.',
      });
    }
  }

  // Lima en una ficha de provincia. `zona` y `distrito` se autocompletan desde la lista cerrada,
  // que es solo de Lima: en una ficha de Tacna traen un valor PRESENTE Y FALSO, que es peor que un
  // nulo porque se puede agrupar por él sin que nada avise.
  if (esDeProvincia(f.lat, f.lng)
    && ((f.zona ?? '').startsWith('Lima') || (f.distrito ?? '').trim() !== '')) {
    out.push({
      clave: 'zona_de_lima', tono: 'aviso',
      texto: `La zona («${f.zona ?? '—'}») y el distrito («${f.distrito ?? '—'}») salieron de la `
        + 'lista cerrada, que es solo de Lima, y el GPS pone esta ficha en provincia. No sirven '
        + 'para agrupar.',
    });
  }

  if (f.registradoEn !== null) {
    const dias = Math.round(
      (new Date(f.registradoEn).getTime() - new Date(`${f.fecha}T12:00:00Z`).getTime())
      / 86400000,
    );
    if (dias > 1) {
      out.push({
        clave: 'viajo_en_cola', tono: 'aviso',
        texto: `La ficha se levantó el ${f.fecha} y llegó al servidor ${dias} días después: viajó `
          + 'en la cola sin señal.',
      });
    }
  }

  if (f.duracionSeg !== null && f.duracionSeg < 30 && f.levantado) {
    out.push({
      clave: 'muy_rapida', tono: 'aviso',
      texto: `${f.duracionSeg} segundos de principio a fin. No alcanza para levantar un puesto.`,
    });
  }
  if (f.duracionSeg !== null && f.duracionSeg > 7200) {
    out.push({
      clave: 'muy_larga', tono: 'aviso',
      texto: 'Más de dos horas: casi seguro es un borrador que se retomó al día siguiente. No '
        + 'sirve para promediar duraciones.',
    });
  }

  return out;
}

/** Puestos con el mismo número en el mismo mercado, día y supervisor: el recorrido se repitió. */
export function puestosRepetidos(
  fichas: readonly FichaMercado[],
): { clave: string; puesto: number; n: number }[] {
  const m = new Map<string, number>();
  for (const f of fichas) {
    if (f.puestoNumero === null) continue;
    const k = `${claveTexto(f.mercadoNombre, STOPWORDS_LUGAR) ?? '?'}||${f.fecha}`
      + `||${claveTexto(f.supervisor) ?? '?'}||${f.puestoNumero}`;
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m.entries()].filter(([, n]) => n > 1).map(([k, n]) => ({
    clave: k, puesto: Number(k.split('||')[3]), n,
  }));
}

/** Cuántas marcas tienen respuesta en la ficha, y cuántas están presentes. Para la fila plegada. */
export function resumenDeFicha(f: FichaMercado): {
  cats: number; marcas: number; presentes: number; quiebres: number; precios: number;
} {
  const presencia = f.ficha.presencia ?? {};
  const estados = Object.values(presencia) as PresenciaMer[];
  return {
    cats: (f.ficha.cats ?? []).length,
    marcas: estados.length,
    presentes: estados.filter((e) => e === 'presente').length,
    quiebres: estados.filter((e) => e === 'quiebre').length,
    precios: (f.ficha.precios ?? []).filter((p) => p.precio !== null).length,
  };
}
